import { useRef, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import styles from './mobile-time-picker.module.scss';

type MobileTimePickerProps = {
  value: Dayjs | null;
  onChange: (value: Dayjs) => void;
  disabled?: boolean;
  status?: 'error' | 'warning';
  className?: string;
};

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const PERIODS: string[] = ['AM', 'PM'];

const ITEM_HEIGHT = 36;

type WheelColumnProps = {
  items: (string | number)[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  disabled?: boolean;
};

function WheelColumn({ items, selectedIndex, onSelect, disabled }: WheelColumnProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const initialScrollDone = useRef(false);

  useEffect(() => {
    if (containerRef.current && !isScrollingRef.current) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
          initialScrollDone.current = true;
        }
      });
    }
  }, [selectedIndex]);

  const handleScroll = () => {
    if (disabled) return;

    isScrollingRef.current = true;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (containerRef.current) {
        const scrollTop = containerRef.current.scrollTop;
        const index = Math.round(scrollTop / ITEM_HEIGHT);
        const clampedIndex = Math.max(0, Math.min(index, items.length - 1));

        containerRef.current.scrollTo({
          top: clampedIndex * ITEM_HEIGHT,
          behavior: 'smooth',
        });

        if (clampedIndex !== selectedIndex) {
          onSelect(clampedIndex);
        }
      }
      isScrollingRef.current = false;
    }, 100);
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.wheelColumn} ${disabled ? styles.disabled : ''}`}
      onScroll={handleScroll}
    >
      <div className={styles.spacer} />
      {items.map((item, index) => (
        <div
          key={index}
          className={`${styles.wheelItem} ${index === selectedIndex ? styles.selected : ''}`}
          onClick={() => !disabled && onSelect(index)}
        >
          {typeof item === 'number' ? String(item).padStart(2, '0') : item}
        </div>
      ))}
      <div className={styles.spacer} />
    </div>
  );
}

export function MobileTimePicker({
  value,
  onChange,
  disabled,
  status,
  className,
}: MobileTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState<Dayjs | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeValue = tempValue ?? value ?? dayjs();

  const hour12 = activeValue.hour() % 12 || 12;
  const minute = activeValue.minute();
  const isPM = activeValue.hour() >= 12;

  const hourIndex = HOURS.indexOf(hour12);
  const minuteIndex = minute;
  const periodIndex = isPM ? 1 : 0;

  useEffect(() => {
    if (isOpen) {
      setTempValue(value || dayjs());
    } else {
      setTempValue(null);
    }
  }, [isOpen, value]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleHourChange = (index: number) => {
    const newHour = HOURS[index];
    const hour24 = isPM ? (newHour === 12 ? 12 : newHour + 12) : newHour === 12 ? 0 : newHour;
    setTempValue(activeValue.hour(hour24));
  };

  const handleMinuteChange = (index: number) => {
    setTempValue(activeValue.minute(index));
  };

  const handlePeriodChange = (index: number) => {
    const newIsPM = index === 1;
    const currentHour = activeValue.hour();
    let newHour: number;

    if (newIsPM && currentHour < 12) {
      newHour = currentHour + 12;
    } else if (!newIsPM && currentHour >= 12) {
      newHour = currentHour - 12;
    } else {
      newHour = currentHour;
    }

    setTempValue(activeValue.hour(newHour));
  };

  const handleConfirm = () => {
    onChange(activeValue);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const displayValue = value ? value.format('h:mm A') : '--:-- --';

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <button
        type="button"
        className={`${styles.trigger} ${status === 'error' ? styles.error : ''} ${disabled ? styles.disabled : ''}`}
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
      >
        {displayValue}
      </button>

      {isOpen && (
        <div className={styles.overlay} onClick={handleCancel}>
          <div ref={dropdownRef} className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
            <div className={styles.header}>
              <button type="button" className={styles.cancelBtn} onClick={handleCancel}>
                Cancel
              </button>
              <button type="button" className={styles.confirmBtn} onClick={handleConfirm}>
                OK
              </button>
            </div>

            <div className={styles.pickerContainer}>
              <div className={styles.selectionHighlight} />

              <WheelColumn
                items={HOURS}
                selectedIndex={hourIndex}
                onSelect={handleHourChange}
                disabled={disabled}
              />

              <div className={styles.separator}>:</div>

              <WheelColumn
                items={MINUTES}
                selectedIndex={minuteIndex}
                onSelect={handleMinuteChange}
                disabled={disabled}
              />

              <WheelColumn
                items={PERIODS}
                selectedIndex={periodIndex}
                onSelect={handlePeriodChange}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
